import { useCallback, useMemo, useRef } from "react";
import type { ArgusSessionContext } from "../../lib/argus/argus-session-context";
import type { ArgusUserInfo } from "../../lib/argus/argus-user";

export interface UseArgusGatewayReturn {
  /** Start a new session */
  startSession: (
    prompt: string,
    options?: {
      model?: string;
      thinkingLevel?: string;
      argusContext?: ArgusSessionContext;
      user?: ArgusUserInfo;
    },
  ) => Promise<{ id: string; sessionFile: string }>;
  /** Send a message to a running session (steer = interrupt, followUp = queue) */
  sendMessage: (
    sessionId: string,
    message: string,
    method?: "steer" | "followUp",
    options?: { user?: ArgusUserInfo },
  ) => Promise<void>;
  /** Stop a session gracefully */
  stopSession: (sessionId: string) => Promise<void>;
  /** Kill a session immediately */
  killSession: (sessionId: string) => Promise<void>;
  /** Get session statistics */
  getStats: (sessionId: string) => Promise<unknown>;
  /** Get session transcript (conversation history) */
  getTranscript: (sessionId: string) => Promise<{ sessionId: string; messages: unknown[] }>;
  /** List sessions */
  listSessions: (status?: "active" | "all") => Promise<unknown>;
  /** Get current queue state */
  getQueue: (sessionId: string) => Promise<{
    steering: string[];
    followUp: string[];
    pendingCount: number;
    /** True when pi-coding-agent has an in-flight turn. */
    streaming: boolean;
    /** Tool call IDs whose tool_execution_end has not yet arrived. */
    openToolCallIds: string[];
  }>;
  /** Clear all queued messages */
  clearQueue: (
    sessionId: string,
  ) => Promise<{ cleared: { steering: string[]; followUp: string[] } }>;
  /** Remove a single queued message by type and index */
  removeFromQueue: (
    sessionId: string,
    type: "steering" | "followUp",
    index: number,
  ) => Promise<void>;
  /** Get filesystem tree */
  getFileTree: (path?: string, depth?: number) => Promise<unknown>;
  /** Read a file from the sandbox */
  readFile: (path: string) => Promise<string>;
}

/**
 * HTTP client hook for the Argus gateway command and query endpoints.
 *
 * All methods use `fetch()` directly — no React Query caching.
 * All methods throw if `hostUrl` or `accessToken` is null.
 *
 * @param hostUrl - Gateway base URL (e.g. `https://<sandbox>.e2b.dev`)
 * @param accessToken - e2b traffic access token
 */
export const useArgusGateway = (
  hostUrl: string | null,
  accessToken: string | null,
): UseArgusGatewayReturn => {
  const hostUrlRef = useRef(hostUrl);
  const accessTokenRef = useRef(accessToken);
  hostUrlRef.current = hostUrl;
  accessTokenRef.current = accessToken;

  const request = useCallback(
    async <T = unknown>(
      method: "GET" | "POST" | "DELETE",
      path: string,
      body?: unknown,
    ): Promise<T> => {
      const url = hostUrlRef.current;
      const token = accessTokenRef.current;

      // `accessToken` is optional — e2b templates without traffic access
      // tokens return null and don't enforce the header. Only `hostUrl`
      // is strictly required.
      if (!url) {
        throw new Error("Gateway not available: missing hostUrl");
      }

      const fullUrl = `${url.replace(/\/+$/, "")}${path}`;
      const headers: Record<string, string> = {};
      if (token) {
        headers["e2b-traffic-access-token"] = token;
      }

      if (body !== undefined) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(fullUrl, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const message = errorBody?.error ?? `HTTP error ${response.status}`;
        throw new Error(message);
      }

      return response.json() as Promise<T>;
    },
    [],
  );

  const requestText = useCallback(async (method: "GET" | "POST", path: string): Promise<string> => {
    const url = hostUrlRef.current;
    const token = accessTokenRef.current;

    if (!url) {
      throw new Error("Gateway not available: missing hostUrl");
    }

    const fullUrl = `${url.replace(/\/+$/, "")}${path}`;
    const headers: Record<string, string> = {};
    if (token) {
      headers["e2b-traffic-access-token"] = token;
    }
    const response = await fetch(fullUrl, {
      method,
      headers,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message = errorBody?.error ?? `HTTP error ${response.status}`;
      throw new Error(message);
    }

    const json = (await response.json()) as { content?: string };
    return json.content ?? "";
  }, []);

  const startSession = useCallback(
    async (
      prompt: string,
      options?: {
        model?: string;
        thinkingLevel?: string;
        argusContext?: ArgusSessionContext;
        user?: ArgusUserInfo;
      },
    ): Promise<{ id: string; sessionFile: string }> => {
      return request<{ id: string; sessionFile: string }>("POST", "/session/cmds/start", {
        prompt,
        model: options?.model,
        thinkingLevel: options?.thinkingLevel,
        argusContext: options?.argusContext,
        user: options?.user,
      });
    },
    [request],
  );

  const sendMessage = useCallback(
    async (
      sessionId: string,
      message: string,
      method: "steer" | "followUp" = "steer",
      options?: { user?: ArgusUserInfo },
    ): Promise<void> => {
      await request("POST", `/session/cmds/steer/${sessionId}`, {
        message,
        method,
        user: options?.user,
      });
    },
    [request],
  );

  const stopSession = useCallback(
    async (sessionId: string): Promise<void> => {
      await request("POST", `/session/cmds/stop/${sessionId}`);
    },
    [request],
  );

  const killSession = useCallback(
    async (sessionId: string): Promise<void> => {
      await request("POST", `/session/cmds/kill/${sessionId}`);
    },
    [request],
  );

  const getStats = useCallback(
    async (sessionId: string): Promise<unknown> => {
      return request("GET", `/session/manage/stats/${sessionId}`);
    },
    [request],
  );

  const getTranscript = useCallback(
    async (sessionId: string): Promise<{ sessionId: string; messages: unknown[] }> => {
      return request<{ sessionId: string; messages: unknown[] }>(
        "GET",
        `/session/manage/transcript/${sessionId}`,
      );
    },
    [request],
  );

  const listSessions = useCallback(
    async (status?: "active" | "all"): Promise<unknown> => {
      const query = status ? `?status=${status}` : "";
      return request("GET", `/session/manage/list${query}`);
    },
    [request],
  );

  const getQueue = useCallback(
    async (
      sessionId: string,
    ): Promise<{
      steering: string[];
      followUp: string[];
      pendingCount: number;
      streaming: boolean;
      openToolCallIds: string[];
    }> => {
      // Older gateway builds don't return `streaming` / `openToolCallIds`;
      // default them so callers can rely on the field always being present.
      const raw = await request<{
        steering: string[];
        followUp: string[];
        pendingCount: number;
        streaming?: boolean;
        openToolCallIds?: string[];
      }>("GET", `/session/queue/${sessionId}`);
      return {
        steering: raw.steering,
        followUp: raw.followUp,
        pendingCount: raw.pendingCount,
        streaming: Boolean(raw.streaming),
        openToolCallIds: raw.openToolCallIds ?? [],
      };
    },
    [request],
  );

  const clearQueue = useCallback(
    async (sessionId: string): Promise<{ cleared: { steering: string[]; followUp: string[] } }> => {
      return request("DELETE", `/session/queue/${sessionId}`);
    },
    [request],
  );

  const removeFromQueue = useCallback(
    async (sessionId: string, type: "steering" | "followUp", index: number): Promise<void> => {
      await request("DELETE", `/session/queue/${sessionId}/${type}/${index}`);
    },
    [request],
  );

  const getFileTree = useCallback(
    async (path?: string, depth?: number): Promise<unknown> => {
      const params = new URLSearchParams();
      if (path) params.set("path", path);
      if (depth !== undefined) params.set("depth", String(depth));
      const query = params.toString();
      return request("GET", `/fs/tree${query ? `?${query}` : ""}`);
    },
    [request],
  );

  const readFile = useCallback(
    async (path: string): Promise<string> => {
      return requestText("GET", `/fs/file?path=${encodeURIComponent(path)}`);
    },
    [requestText],
  );

  // Freeze the return shape into a memoised object. Every individual
  // callback is already memoised with `useCallback`, but returning a fresh
  // object literal from the hook on every render broke downstream
  // `useEffect` dependencies — the transcript-load effect in
  // `useArgusConnection` was observing `gateway` and re-running in a loop
  // (flicker + a torrent of `GET /session/manage/transcript/...` calls).
  return useMemo(
    () => ({
      startSession,
      sendMessage,
      stopSession,
      killSession,
      getStats,
      getTranscript,
      listSessions,
      getQueue,
      clearQueue,
      removeFromQueue,
      getFileTree,
      readFile,
    }),
    [
      startSession,
      sendMessage,
      stopSession,
      killSession,
      getStats,
      getTranscript,
      listSessions,
      getQueue,
      clearQueue,
      removeFromQueue,
      getFileTree,
      readFile,
    ],
  );
};
